import React, { useState, useEffect } from 'react';
import { FileText, Download, Users, Calendar, Scale } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VacationReportsAdmin = ({ token, user }) => {
  const [vacationsSummary, setVacationsSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [annualPreview, setAnnualPreview] = useState(null);
  const [loadingAnnual, setLoadingAnnual] = useState(false);
  const [downloadingAnnual, setDownloadingAnnual] = useState(false);

  useEffect(() => {
    if (token) {
      fetchVacationsSummary();
      fetchEmployees();
      fetchAnnualPreview(selectedYear);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchAnnualPreview(selectedYear);
    }
  }, [selectedYear]);

  const fetchVacationsSummary = async () => {
    try {
      const response = await axios.get(`${API}/reports/vacations-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVacationsSummary(response.data);
    } catch (error) {
      console.error('Error fetching vacations summary:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/admin/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleDownloadVacationsPdf = async (employeeId, employeeName) => {
    setDownloadingPdf(employeeId);
    try {
      const response = await axios.get(
        `${API}/reports/employee-vacations/${employeeId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vacaciones_${employeeName.replace(/\s+/g, '_')}_${new Date().getFullYear()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar el PDF');
      console.error('Download error:', error);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadAllVacationsPdf = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/all-vacations/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vacaciones_todos_empleados_${new Date().getFullYear()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF de todas las vacaciones descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar el PDF');
      console.error('Download error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnualPreview = async (year) => {
    setLoadingAnnual(true);
    try {
      const response = await axios.get(`${API}/reports/annual-legal/${year}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnualPreview(response.data);
    } catch (error) {
      console.error('Error fetching annual preview:', error);
    } finally {
      setLoadingAnnual(false);
    }
  };

  const handleDownloadAnnualPdf = async () => {
    setDownloadingAnnual(true);
    try {
      const response = await axios.get(
        `${API}/reports/annual-legal/${selectedYear}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe_anual_legal_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Informe Anual Legal ${selectedYear} descargado correctamente`);
    } catch (error) {
      toast.error('Error al descargar el informe anual');
      console.error('Download error:', error);
    } finally {
      setDownloadingAnnual(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Annual Legal Report Card */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800">Informe Anual Legal</span>
              <Badge className="bg-blue-600 text-white ml-2">Cumplimiento RD-ley 8/2019</Badge>
            </span>
            <div className="flex items-center gap-3">
              <Select 
                value={selectedYear.toString()} 
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger className="w-28" data-testid="annual-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleDownloadAnnualPdf} 
                disabled={downloadingAnnual}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="download-annual-pdf"
              >
                <Download className="h-4 w-4 mr-2" />
                {downloadingAnnual ? 'Generando...' : 'Descargar PDF Legal'}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingAnnual ? (
            <p className="text-center text-slate-500 py-4">Cargando datos del año {selectedYear}...</p>
          ) : annualPreview ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 text-center shadow-sm border">
                  <p className="text-sm text-slate-600">Total Empleados</p>
                  <p className="text-3xl font-bold text-slate-800">{annualPreview.total_employees}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm border">
                  <p className="text-sm text-indigo-600">Horas Trabajadas</p>
                  <p className="text-3xl font-bold text-indigo-700">{annualPreview.total_hours?.toLocaleString()}h</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm border">
                  <p className="text-sm text-green-600">Días Vacaciones</p>
                  <p className="text-3xl font-bold text-green-700">{annualPreview.total_vacation_days}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm border">
                  <p className="text-sm text-amber-600">Promedio/Empleado</p>
                  <p className="text-3xl font-bold text-amber-700">{annualPreview.avg_hours_per_employee?.toFixed(0)}h</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-700 mb-3">Resumen por Empleado - {selectedYear}</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empleado</TableHead>
                      <TableHead>Departamento</TableHead>
                      <TableHead className="text-center">Horas Año</TableHead>
                      <TableHead className="text-center">Días Trab.</TableHead>
                      <TableHead className="text-center">Vacaciones</TableHead>
                      <TableHead className="text-center">Prom/Día</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {annualPreview.employees?.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{emp.name}</p>
                            <p className="text-xs text-slate-500">{emp.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{emp.department}</Badge>
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {emp.total_hours?.toFixed(0)}h
                        </TableCell>
                        <TableCell className="text-center">
                          {emp.work_days}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-green-100 text-green-700">{emp.vacation_days} días</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {emp.avg_hours_day?.toFixed(1)}h
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Nota Legal:</strong> Este informe debe conservarse durante 4 años según el Real Decreto-ley 8/2019. 
                  El PDF incluye la declaración de cumplimiento legal y espacio para firma del responsable.
                </p>
              </div>
            </>
          ) : (
            <p className="text-center text-slate-500 py-4">No hay datos disponibles para {selectedYear}</p>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Informe de Vacaciones - Todos los Empleados
            </span>
            <Button onClick={handleDownloadAllVacationsPdf} disabled={loading}>
              <Download className="h-4 w-4 mr-2" />
              {loading ? 'Descargando...' : 'Descargar PDF General'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {vacationsSummary ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-600">Total Empleados</p>
                  <p className="text-3xl font-bold text-blue-700">{vacationsSummary.total_employees}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-600">Días Aprobados Total</p>
                  <p className="text-3xl font-bold text-green-700">
                    {vacationsSummary.employees?.reduce((acc, emp) => acc + emp.approved_vacation_days, 0) || 0}
                  </p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-yellow-600">Pendientes de Aprobación</p>
                  <p className="text-3xl font-bold text-yellow-700">
                    {vacationsSummary.employees?.reduce((acc, emp) => acc + emp.pending_vacation_days, 0) || 0}
                  </p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleado</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">Usados</TableHead>
                    <TableHead className="text-center">Pendientes</TableHead>
                    <TableHead className="text-center">Disponibles</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vacationsSummary.employees?.map((emp) => (
                    <TableRow key={emp.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{emp.name}</p>
                          <p className="text-sm text-slate-500">{emp.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{emp.department}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{emp.total_vacation_days}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-green-100 text-green-700">{emp.used_vacation_days}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-yellow-100 text-yellow-700">{emp.pending_vacation_days}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-blue-100 text-blue-700">{emp.remaining_vacation_days}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadVacationsPdf(emp.id, emp.name)}
                          disabled={downloadingPdf === emp.id}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          {downloadingPdf === emp.id ? '...' : 'PDF'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <p className="text-center text-slate-500 py-4">Cargando resumen de vacaciones...</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VacationReportsAdmin;
